import React, { useMemo, useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '../../lib/utils';
import { parseAccent, plainText } from '../primitives/SplitText';

/*
 * Pinned paragraph whose words light up one by one as you scroll through it.
 * Adapted from Magic UI "Text Reveal" (MIT) via 21st.dev:
 * https://21st.dev/dillionverma/text-reveal (upstream version, single scroll ref).
 * Supports *accent* words like SplitText.
 */
// A function, not useTransform(progress, range, [0, 1]): motion 12 hands the range form to a
// native scroll timeline whose keyframes stop at the range, so lit words faded out again
const Word = ({ progress, range: [from, to], accent, children }) => {
  const opacity = useTransform(progress, v => Math.min(1, Math.max(0, (v - from) / (to - from))));
  return (
    <span className="relative inline-block">
      {/* Both layers share the accent font so the dim copy is exactly as wide as the lit one */}
      <span aria-hidden="true" className={cn('absolute text-foreground/15', accent && 'accent')}>{children}</span>
      <motion.span aria-hidden="true" style={{ opacity }} className={cn('text-foreground', accent && 'accent')}>
        {children}
      </motion.span>
    </span>
  );
};

const TextReveal = ({ text, className, textClassName, children }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const words = useMemo(() => parseAccent(text).filter(w => !w.space), [text]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  if (reduced) {
    return (
      <div ref={ref} className={cn('py-[var(--section-y)]', className)}>
        <div className="container-site">
          {children}
          <p className={textClassName}>{plainText(text)}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={cn('relative h-[220vh]', className)}>
      <div className="sticky top-0 flex h-screen items-center">
        <div className="container-site">
          {children}
          <p className={cn('flex flex-wrap gap-x-[0.26em]', textClassName)}>
            <span className="sr-only">{plainText(text)}</span>
            {words.map((w, i) => {
              const start = i / words.length;
              return (
                <Word key={i} progress={scrollYProgress} range={[start * 0.85, (start + 1 / words.length) * 0.85]} accent={w.accent}>
                  {w.word}
                </Word>
              );
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TextReveal;
